import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  map,
  NEVER,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
} from 'rxjs';
import { CampaignEmailCredential } from 'src/app/shared/models/campaign/campaign-account';
import { CampaignService } from 'src/app/shared/services/requests/campaign.service';
import { ValidationCheckStatus } from '../types';

@Injectable()
export class EmailCredentialVerificationService {
  constructor(private campaignService: CampaignService) {}

  private checkConnectionSubject = new Subject<void>();
  private verifyTrackingDomainSubject = new Subject<void>();

  private connectionValidationStatusSubject = new BehaviorSubject<ValidationCheckStatus>('idle');

  private settingsAcquiringCallback?: () => CampaignEmailCredential;

  setSettingsAcquiringCallback(callback: () => CampaignEmailCredential): void {
    this.settingsAcquiringCallback = callback;
  }

  checkConnection(): void {
    this.checkConnectionSubject.next();
  }

  checkTrackingDomain(): void {
    this.verifyTrackingDomainSubject.next();
  }

  /** Subscribe only once. Unsubscribe to stop. */
  runValidationTask(): Observable<null> {
    return this.checkConnectionSubject.pipe(
      switchMap(() => {
        const settings = this?.settingsAcquiringCallback();

        return this.campaignService.verifyEmailAccountCredentials(settings).pipe(
          map((valid: boolean) => {
            return valid ? 'ok' : 'failed';
          }),
          catchError(() => of('idle')),
          startWith<ValidationCheckStatus>('loading'),
        );
      }),
      map((status: ValidationCheckStatus) => {
        this.connectionValidationStatusSubject.next(status);

        return null;
      }),
    );
  }

  getCredentialVerificationStatus$(): Observable<ValidationCheckStatus> {
    return this.connectionValidationStatusSubject.asObservable();
  }

  getTrackingDomainCheckStatus$(): Observable<ValidationCheckStatus> {
    return this.verifyTrackingDomainSubject.pipe(
      switchMap(() => {
        const settings = this.settingsAcquiringCallback();
        if (!settings?.trackingDomain) return NEVER;

        this.campaignService.verifyTrackingDomain(settings.trackingDomain).pipe(
          map((valid: boolean) => {
            return valid ? 'ok' : 'failed';
          }),
          catchError(() => of('idle')),
          startWith<ValidationCheckStatus>('loading'),
        );
      }),
      startWith<ValidationCheckStatus>('idle'),
    );
  }
}
